import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Send, ArrowRight, Sparkles, RotateCcw, Shield } from 'lucide-react';

import { PageShell } from '@/components/brand/PageShell';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { useAuth } from '@/contexts/AuthContext';
import { capture } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Suggested prompts — 10 curated, rotated in groups of 4                     */
/* -------------------------------------------------------------------------- */

const SUGGESTED = [
  { icon: '🍼', label: 'Newborn essentials checklist',  prompt: 'What do I actually need for the first month with a newborn? Keep it practical.' },
  { icon: '🎁', label: 'Baby shower gift ideas',         prompt: "What are thoughtful gift ideas for someone's baby shower? Mix practical and heirloom." },
  { icon: '📝', label: 'What goes in a registry?',       prompt: "I'm thinking about starting a baby registry. What categories should I cover and how many items in each?" },
  { icon: '🤰', label: 'Preparing for delivery day',     prompt: 'How do I prepare for delivery day — what should be in my hospital bag?' },
  { icon: '🛏️', label: 'Baby sleep setup',                prompt: "How should I set up my baby's sleep space safely? What do I actually need?" },
  { icon: '🤱', label: 'Breastfeeding essentials',        prompt: 'What breastfeeding essentials should I have ready? Include the nice-to-haves.' },
  { icon: '🏠', label: 'Registry for a small apartment',  prompt: "We live in a small apartment in India. What's worth buying and what can I skip?" },
  { icon: '🚫', label: 'What should I avoid buying?',     prompt: 'Be honest — what baby products are overhyped and not worth buying?' },
  { icon: '✨', label: 'Gifts that are actually useful',  prompt: 'What gifts do parents actually use after the baby arrives, not just the cute ones?' },
  { icon: '🪔', label: 'Indian baby shower (godh bharai)',prompt: 'What are thoughtful gift ideas for an Indian baby shower or godh bharai?' },
];

const ROTATE_SET_SIZE = 4;
const ROTATE_INTERVAL_MS = 4500;

/* -------------------------------------------------------------------------- */
/*  Lightweight markdown: bold, italics, line breaks                           */
/* -------------------------------------------------------------------------- */

const renderInline = (text) => {
  const parts = [];
  let i = 0;
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let m;
  let last = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('**')) parts.push(<strong key={i++}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith('*')) parts.push(<em key={i++} className="text-clay-700">{token.slice(1, -1)}</em>);
    else if (token.startsWith('`')) parts.push(<code key={i++} className="px-1 rounded bg-cream-200 text-[13px]">{token.slice(1, -1)}</code>);
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const RenderedReply = ({ text }) => {
  const paragraphs = (text || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <>
      {paragraphs.map((p, idx) => (
        <p key={idx} className={`text-[15px] text-ink-soft leading-[1.65] ${idx > 0 ? 'mt-3' : ''}`}>
          {renderInline(p).map((node, j) =>
            typeof node === 'string'
              ? node.split('\n').flatMap((line, k, arr) => (k < arr.length - 1 ? [line, <br key={`${idx}-${j}-${k}`} />] : [line]))
              : node
          )}
        </p>
      ))}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*  Typing indicator                                                           */
/* -------------------------------------------------------------------------- */

const TypingDots = () => (
  <div className="inline-flex items-center gap-1.5 py-2" aria-label="JB is thinking">
    <span className="h-1.5 w-1.5 rounded-full bg-clay-400 animate-bounce" style={{ animationDelay: '0ms' }} />
    <span className="h-1.5 w-1.5 rounded-full bg-clay-400 animate-bounce" style={{ animationDelay: '120ms' }} />
    <span className="h-1.5 w-1.5 rounded-full bg-clay-400 animate-bounce" style={{ animationDelay: '240ms' }} />
  </div>
);

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const AskJB = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState([]); // [{role, content, cta?}]
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Rotating-prompt state — only visible in the empty/initial state.
  // setIndex advances by ROTATE_SET_SIZE every ROTATE_INTERVAL_MS; the
  // 4 currently-visible prompts crossfade together. Paused while the
  // user is hovering the grid so they can actually read the cards.
  const [rotateIndex, setRotateIndex] = useState(0);
  const [rotatePaused, setRotatePaused] = useState(false);

  const conversationRef = useRef(null);
  const inputRef = useRef(null);
  const hasMessages = messages.length > 0;

  // First name for personalisation (H1, subtitle, composer placeholder).
  // Falls back to email-prefix then 'there' for Google-OAuth users with no
  // name set, so we never render "Hi 's parenting companion".
  const firstName = (() => {
    if (!user) return '';
    const fromName = (user.name || '').trim().split(/\s+/)[0];
    if (fromName) return fromName;
    const fromEmail = (user.email || '').split('@')[0];
    return fromEmail || '';
  })();

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (!conversationRef.current) return;
    conversationRef.current.scrollTo({
      top: conversationRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, sending]);

  // Focus input when conversation opens
  useEffect(() => {
    if (hasMessages && inputRef.current) inputRef.current.focus();
  }, [hasMessages]);

  // Deep-link: if arriving from homepage with ?q=..., auto-send once and
  // clear the param so refresh doesn't replay it. Waits for the auth
  // context to finish loading first — otherwise a signed-in user's first
  // message goes out without the token and silently loses personalization.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (authLoading || autoSentRef.current) return;
    const q = searchParams.get('q');
    if (!q || messages.length > 0 || sending) return;
    autoSentRef.current = true;
    setSearchParams({}, { replace: true });
    send(q);
  }, [authLoading]); // eslint-disable-line

  // Rotate the 4 visible prompts every ROTATE_INTERVAL_MS while the user
  // hasn't started a conversation and isn't hovering the grid.
  useEffect(() => {
    if (hasMessages || rotatePaused) return;
    const id = setInterval(() => {
      setRotateIndex((i) => (i + ROTATE_SET_SIZE) % SUGGESTED.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasMessages, rotatePaused]);

  // The 4 prompts currently on screen. Wraps if we're near the end of the
  // pool so we always show exactly ROTATE_SET_SIZE cards.
  const visiblePrompts = useMemo(() => {
    const out = [];
    for (let i = 0; i < ROTATE_SET_SIZE; i++) {
      out.push(SUGGESTED[(rotateIndex + i) % SUGGESTED.length]);
    }
    return out;
  }, [rotateIndex]);

  const send = async (text) => {
    const content = (text || '').trim();
    if (!content || sending) return;

    const userMsg = { role: 'user', content };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setDraft('');
    setSending(true);
    setError(null);
    capture('askjb_message_sent', { turn: nextMessages.length });

    try {
      const apiMessages = nextMessages.map((m) => ({ role: m.role, content: m.content }));
      // Send the auth token when signed in so the backend can personalize
      // (greet by name, reference what's already in the user's registry).
      // Anonymous calls send no token and the backend falls back to generic.
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/ask-jb`,
        { messages: apiMessages },
        { headers, timeout: 35000 }
      );
      const { reply, cta } = response.data || {};
      setMessages((p) => [...p, { role: 'assistant', content: reply || 'I went quiet for a moment — try asking again?', cta }]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not reach JB just now. Try again in a sec?');
      setMessages((p) => [
        ...p,
        { role: 'assistant', content: "I'm having a quiet moment — give me a beat and try again?" },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setDraft('');
    setError(null);
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send(draft);
  };

  return (
    <PageShell stickyMobileCTA>
      <div className="container-page py-12 sm:py-16">
        {/* Hero — collapses to a compact strip once the conversation starts */}
        <header
          className={`text-center transition-all duration-500 ${
            hasMessages ? 'max-w-3xl mx-auto mb-6' : 'max-w-2xl mx-auto mb-10'
          }`}
        >
          <LogoMark size={hasMessages ? 24 : 36} className="text-clay-700 mx-auto mb-5 transition-all" />
          <div className="eyebrow mb-3">Ask JB</div>
          {!hasMessages ? (
            <>
              <h1 className="font-display text-display-xl text-ink leading-[1.05]">
                {firstName ? (
                  <>
                    {firstName}'s parenting
                    <br />
                    <span className="italic text-clay-700" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
                      companion.
                    </span>
                  </>
                ) : (
                  <>
                    Your parenting
                    <br />
                    <span className="italic text-clay-700" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
                      companion.
                    </span>
                  </>
                )}
              </h1>
              <p className="mt-5 text-lg text-ink-soft leading-relaxed">
                {firstName
                  ? `Hi ${firstName} — I'll personalise around what's already in your registry. Ask anything.`
                  : "Personalised guidance for pregnancy, baby prep, and gifting — anytime. I'm warm, calm, and free. Ask anything."}
              </p>
            </>
          ) : (
            <h1 className="font-display text-display-md text-ink leading-snug">
              Ask JB — <span className="italic text-clay-700">your parenting companion</span>
            </h1>
          )}
        </header>

        <div className="max-w-3xl mx-auto">
          {/* Suggested prompts — rotating, initial state only.
              Wrapper handles hover-pause; the inner div is keyed on
              rotateIndex so React unmounts/remounts the 4 cards each
              cycle, which triggers the animate-fade-up keyframe for
              a soft cross-fade. */}
          {!hasMessages && (
            <div
              className="mb-6 animate-fade-up"
              onMouseEnter={() => setRotatePaused(true)}
              onMouseLeave={() => setRotatePaused(false)}
              onFocus={() => setRotatePaused(true)}
              onBlur={() => setRotatePaused(false)}
            >
              <div
                key={rotateIndex}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fade-up"
              >
                {visiblePrompts.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => send(s.prompt)}
                    className="group flex items-start gap-3 p-4 rounded-2xl bg-cream-50 border border-border/70 hover:border-clay-200 hover:shadow-soft-1 text-left transition-all"
                    data-testid={`suggested-${s.label}`}
                  >
                    <span className="text-lg leading-none mt-0.5 shrink-0" aria-hidden>{s.icon}</span>
                    <div>
                      <p className="text-[14.5px] font-medium text-ink leading-snug">{s.label}</p>
                      <p className="mt-1 text-[12.5px] text-ink-muted leading-snug line-clamp-2">{s.prompt}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Tiny progress dots — 1 dot per group of 4 prompts */}
              <div className="mt-4 flex items-center justify-center gap-1.5" aria-hidden>
                {Array.from({ length: Math.ceil(SUGGESTED.length / ROTATE_SET_SIZE) }).map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 rounded-full transition-all duration-500 ${
                      Math.floor(rotateIndex / ROTATE_SET_SIZE) === i
                        ? 'w-6 bg-clay-600'
                        : 'w-1.5 bg-clay-200'
                    }`}
                  />
                ))}
              </div>

              {/* Privacy line — under the prompts, before the composer */}
              <p className="mt-5 flex items-center justify-center gap-1.5 text-[12px] text-ink-muted">
                <Shield className="h-3 w-3" />
                {user
                  ? "Signed in — I'll personalise around your name and registry. Nothing is shared outside Joy Basket."
                  : "Anonymous chat. Conversations aren't saved between visits."}
              </p>
            </div>
          )}

          {/* Conversation */}
          {hasMessages && (
            <div
              ref={conversationRef}
              className="space-y-5 max-h-[60vh] overflow-y-auto pr-2 -mr-2 mb-5"
            >
              {messages.map((m, i) =>
                m.role === 'user' ? (
                  <div key={i} className="flex justify-end animate-fade-up">
                    <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-ink text-cream-50 px-4 py-3 text-[15px] leading-relaxed shadow-soft-1">
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex gap-3 animate-fade-up">
                    <span className="shrink-0 h-9 w-9 rounded-full bg-clay-100 text-clay-700 inline-flex items-center justify-center mt-0.5">
                      <LogoMark size={16} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.16em] text-clay-700 mb-1">JB</p>
                      <div className="rounded-2xl rounded-tl-md bg-cream-50 border border-border/70 px-4 py-3 shadow-soft-1">
                        <RenderedReply text={m.content} />
                        {m.cta && (
                          <div className="mt-4 pt-3 border-t border-border/60">
                            <BrandButton
                              variant="primary"
                              size="sm"
                              onClick={() => navigate(m.cta.href)}
                            >
                              {m.cta.label}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </BrandButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}
              {sending && (
                <div className="flex gap-3 animate-fade-up">
                  <span className="shrink-0 h-9 w-9 rounded-full bg-clay-100 text-clay-700 inline-flex items-center justify-center mt-0.5">
                    <LogoMark size={16} />
                  </span>
                  <div className="rounded-2xl rounded-tl-md bg-cream-50 border border-border/70 px-4 py-3 shadow-soft-1">
                    <TypingDots />
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="mb-3 text-center text-sm text-clay-700">{error}</p>
          )}

          {/* Composer */}
          <form
            onSubmit={handleSubmit}
            className="relative bg-cream-50 rounded-3xl border border-border/70 shadow-soft-1 focus-within:border-clay-300 focus-within:ring-2 focus-within:ring-clay-500/30 transition-all"
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                // auto-grow
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 180)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder={
                hasMessages
                  ? 'Reply to JB…'
                  : firstName
                    ? `Ask anything, ${firstName} — baby prep, registry, gifts…`
                    : 'Ask anything — baby prep, registry, gifts…'
              }
              disabled={sending}
              className="w-full px-5 py-4 pr-14 bg-transparent border-0 outline-none resize-none text-[15px] text-ink placeholder:text-ink-muted leading-relaxed rounded-3xl"
              data-testid="ask-jb-input"
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              className="absolute right-3 bottom-3 h-10 w-10 rounded-full bg-clay-600 hover:bg-clay-700 disabled:opacity-40 disabled:cursor-not-allowed text-cream-50 inline-flex items-center justify-center transition-all shadow-soft-1"
              aria-label="Send"
              data-testid="ask-jb-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>

          {/* Below-composer row */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-muted">
            <p className="leading-relaxed text-center sm:text-left">
              JB is a friendly guide, not a doctor. For medical or legal questions, please ask a professional.
            </p>
            {hasMessages && (
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1.5 text-ink-soft hover:text-clay-700 transition-colors shrink-0"
              >
                <RotateCcw className="h-3 w-3" /> Start over
              </button>
            )}
          </div>

          {/* Quiet footer — alternative for in-depth reading */}
          {!hasMessages && (
            <div className="mt-14 text-center">
              <div className="hairline mb-8" />
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink-muted mb-4">
                <Sparkles className="h-3 w-3" />
                Prefer to read?
              </div>
              <h2 className="font-display text-display-md text-ink leading-snug">
                The long-form{' '}
                <span className="italic text-clay-700">Baby Registry Guide.</span>
              </h2>
              <p className="mt-3 text-ink-soft max-w-md mx-auto">
                Chapters on what, when, and how to share — for the parents who'd rather skim than chat.
              </p>
              <div className="mt-6">
                <BrandButton variant="outline" onClick={() => navigate('/baby-registry-guide')}>
                  Read the guide
                  <ArrowRight className="h-4 w-4" />
                </BrandButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default AskJB;
